// SPDX-License-Identifier: BUSL-1.1

/**
 * Pi Session panel — renders the structured execution metadata that the pi
 * CLI runner persists into `ops_agent_jobs.output_payload.piSession`.
 *
 * Surfaces the model, token breakdown, cost (locally estimated + pi-reported),
 * turn / tool counts, and the path to the JSONL transcript on the runner host.
 * PIO-09 will turn `sessionFilePath` into a download link.
 *
 * Color tokens and Badge / typography style match the Activity Events tab
 * (see EVENT_KIND_COLORS in agent-detail.tsx).
 */

import type * as React from 'react';
import { Badge } from '@/components/ui/badge';
import type { PiSession } from '@/types/api';

/**
 * Provider key → Badge background/foreground classes.
 *
 * Falls back to a neutral chip when the provider is unknown so we never
 * crash on a new vendor showing up in production data.
 */
export const PROVIDER_BADGE_COLORS: Record<string, string> = {
  minimax: 'bg-violet-900/40 text-violet-300',
  anthropic: 'bg-orange-900/40 text-orange-300',
  openai: 'bg-emerald-900/40 text-emerald-300',
};

const NEUTRAL_BADGE = 'bg-neutral-700 text-neutral-300';

/** Resolve a provider key to its Badge classes (never throws). */
export function providerBadgeClass(provider: string | undefined): string {
  if (!provider) return NEUTRAL_BADGE;
  return PROVIDER_BADGE_COLORS[provider.toLowerCase()] ?? NEUTRAL_BADGE;
}

/** Format a token count with locale-aware thousands separators. */
function fmtTokens(n: number | undefined): string {
  if (n == null) return '-';
  return n.toLocaleString();
}

/** Format a USD amount to four decimals, or `-` when missing. */
function fmtUsd(n: number | undefined): string {
  if (n == null) return '-';
  return `$${n.toFixed(4)}`;
}

interface TokenCellProps {
  label: string;
  value: number | undefined;
}

function TokenCell({ label, value }: TokenCellProps): React.ReactElement {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-neutral-500">{label}</dt>
      <dd className="mt-0.5 font-mono text-sm text-neutral-200">{fmtTokens(value)}</dd>
    </div>
  );
}

export interface PiSessionPanelProps {
  session: PiSession;
}

export function PiSessionPanel({ session }: PiSessionPanelProps): React.ReactElement {
  const providerClass = providerBadgeClass(session.provider);
  const hasPiReportedCost = (session.costUsd ?? 0) > 0;

  return (
    <section
      data-testid="pi-session-panel"
      className="rounded-md border border-neutral-800 bg-neutral-900/50 p-4"
    >
      {/* Header — model + provider */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium text-neutral-300">Pi Session</h3>
        <Badge className={providerClass}>{session.model}</Badge>
        <span className="text-xs text-neutral-500">{session.provider}</span>
      </div>

      {/* Token breakdown */}
      <dl className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <TokenCell label="Input" value={session.inputTokens} />
        <TokenCell label="Output" value={session.outputTokens} />
        <TokenCell label="Cache R" value={session.cacheReadTokens} />
        <TokenCell label="Cache W" value={session.cacheWriteTokens} />
        <TokenCell label="Total" value={session.totalTokens} />
      </dl>

      {/* Cost row */}
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <div>
          <span className="text-xs uppercase tracking-wide text-neutral-500">
            Estimated:{' '}
          </span>
          <span className="font-mono text-sm text-green-400">
            {fmtUsd(session.estimatedCostUsd)}
          </span>
        </div>
        {hasPiReportedCost && (
          <div className="text-xs text-neutral-500">
            Pi-reported:{' '}
            <span className="font-mono text-neutral-400">{fmtUsd(session.costUsd)}</span>
          </div>
        )}
      </div>

      {/* Stats pills */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {session.turnCount != null && (
          <span className="inline-flex items-center rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300">
            {session.turnCount} {session.turnCount === 1 ? 'turn' : 'turns'}
          </span>
        )}
        {session.toolCallCount != null && (
          <span className="inline-flex items-center rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300">
            {session.toolCallCount}{' '}
            {session.toolCallCount === 1 ? 'tool call' : 'tool calls'}
          </span>
        )}
        {session.stopReason && (
          <span className="inline-flex items-center rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300">
            stop: {session.stopReason}
          </span>
        )}
      </div>

      {/* Tool name chips */}
      {session.toolNames && session.toolNames.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs uppercase tracking-wide text-neutral-500">Tools:</span>
          {session.toolNames.map((tool, idx) => (
            <Badge
              key={`${tool}-${idx}`}
              className="bg-blue-900/40 text-blue-300"
            >
              {tool}
            </Badge>
          ))}
        </div>
      )}

      {/* Session file path (PIO-09 will make this a download link) */}
      {session.sessionFilePath && (
        <div className="mt-3 border-t border-neutral-800 pt-3">
          <div className="text-xs uppercase tracking-wide text-neutral-500">
            Session file
          </div>
          <code className="mt-1 block break-all font-mono text-xs text-neutral-400">
            {session.sessionFilePath}
          </code>
        </div>
      )}

      {/* Session ID */}
      <div className="mt-3 text-xs text-neutral-500">
        Session ID: <code className="font-mono text-neutral-400">{session.sessionId}</code>
      </div>
    </section>
  );
}
